import React, { useState, useEffect, useMemo } from 'react';
import { Download, Loader2, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { stockAPI, branchAPI } from '../../services/api';
import DateRangeFilter, { filterByDateRange } from './DateRangeFilter';

const COLORS = ['#24549c', '#3a6fc4', '#FF9800', '#4CAF50', '#E65100', '#6A1B9A', '#1a3d73'];

const StockReport = () => {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('product');
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [s, b] = await Promise.all([stockAPI.getAll(), branchAPI.getAll().catch(() => [])]);
        setItems(s); setBranches(b);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const filteredItems = useMemo(() => filterByDateRange(items, dateRange), [items, dateRange]);
  const totalValue = filteredItems.reduce((s, i) => s + (i.value || 0), 0);
  const totalQty = filteredItems.reduce((s, i) => s + (i.quantity || 0), 0);
  const lowStock = filteredItems.filter(i => i.status === 'Low Stock').length;
  const outOfStock = filteredItems.filter(i => i.status === 'Out of Stock').length;

  // Product-wise aggregation
  const productData = useMemo(() => {
    const map = {};
    filteredItems.forEach(i => {
      const name = i.product_name || 'Unknown';
      if (!map[name]) map[name] = { name, value: 0, quantity: 0, batches: 0, avgCost: 0, totalCost: 0 };
      map[name].value += (i.value || 0);
      map[name].quantity += (i.quantity || 0);
      map[name].batches++;
      map[name].totalCost += (i.cost_price || 0) * (i.quantity || 0);
    });
    return Object.values(map).map(p => ({ ...p, avgCost: p.quantity > 0 ? Math.round(p.totalCost / p.quantity) : 0 })).sort((a, b) => b.value - a.value);
  }, [filteredItems]);

  // Status breakdown
  const statusData = useMemo(() => {
    const map = {};
    filteredItems.forEach(i => {
      if (!map[i.status]) map[i.status] = { name: i.status, value: 0, count: 0 };
      map[i.status].value += (i.value || 0);
      map[i.status].count++;
    });
    return Object.values(map);
  }, [filteredItems]);

  // Branch-wise
  const branchData = useMemo(() => {
    const map = {};
    filteredItems.forEach(i => {
      const br = branches.find(b => b.id === i.branch)?.name || i.branch || 'Unknown';
      if (!map[br]) map[br] = { name: br, value: 0, quantity: 0, items: 0 };
      map[br].value += (i.value || 0);
      map[br].quantity += (i.quantity || 0);
      map[br].items++;
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [filteredItems, branches]);

  const currentData = view === 'product' ? productData : view === 'status' ? statusData : branchData;

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(r => Object.values(r).join(','));
    const blob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${filename}.csv`; a.click();
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Stock Analytics</h2>
          <p className="text-xs text-gray-500">Inventory value, product-wise stock, branch distribution & alerts</p>
        </div>
        <button onClick={() => exportCSV(currentData, `stock-${view}`)} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}><Download className="w-3.5 h-3.5" /> Export CSV</button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
        {[
          { l: 'Total Inventory Value', v: `₹${(totalValue / 100000).toFixed(1)}L` },
          { l: 'Total Quantity', v: totalQty.toLocaleString() },
          { l: 'Stock Entries', v: items.length },
          { l: 'Low Stock', v: lowStock, warn: lowStock > 0 },
          { l: 'Out of Stock', v: outOfStock, warn: outOfStock > 0 },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center gap-2">
              <p className={`text-lg font-bold ${s.warn ? 'text-red-600' : 'text-gray-900'}`} style={{ fontFamily: 'DM Sans' }}>{s.v}</p>
              {s.warn && <AlertTriangle className="w-4 h-4 text-red-500" />}
            </div>
            <p className="text-[10px] text-gray-500">{s.l}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <DateRangeFilter onChange={setDateRange} />
        <div className="flex gap-1.5 ml-auto">
          {[{ id: 'product', label: 'Product-wise' }, { id: 'status', label: 'Status-wise' }, { id: 'branch', label: 'Branch-wise' }].map(t => (
            <button key={t.id} onClick={() => setView(t.id)} className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${view === t.id ? 'text-white' : 'text-gray-600 bg-gray-100'}`} style={view === t.id ? { backgroundColor: '#24549c' } : {}}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Value & Quantity</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={currentData.slice(0, 8)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9CA3AF' }} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} />
              <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '11px' }} />
              <Legend wrapperStyle={{ fontSize: '10px' }} />
              <Bar dataKey="value" name="Value (₹)" fill="#24549c" radius={[3, 3, 0, 0]} />
              <Bar dataKey={view === 'product' ? 'quantity' : 'count'} name={view === 'product' ? 'Quantity' : 'Count'} fill="#4CAF50" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Value Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={currentData.filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="value" nameKey="name">
                {currentData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={v => `₹${v.toLocaleString()}`} contentStyle={{ borderRadius: '8px', fontSize: '11px' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2">
            {currentData.filter(d => d.value > 0).slice(0, 7).map((d, i) => (
              <span key={i} className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />{d.name}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-gray-100 bg-gray-50">
            {['Name', ...(view === 'product' ? ['Batches', 'Quantity', 'Avg Cost'] : view === 'branch' ? ['Items', 'Quantity'] : ['Count']), 'Value (₹)'].map(h => (
              <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {currentData.map((r, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="px-4 py-3 font-semibold text-gray-900">{r.name}</td>
                {view === 'product' && <><td className="px-4 py-3 text-gray-500">{r.batches}</td><td className="px-4 py-3 text-gray-700">{r.quantity}</td><td className="px-4 py-3 text-gray-500">₹{r.avgCost?.toLocaleString()}</td></>}
                {view === 'branch' && <><td className="px-4 py-3 text-gray-500">{r.items}</td><td className="px-4 py-3 text-gray-700">{r.quantity}</td></>}
                {view === 'status' && <td className="px-4 py-3 text-gray-500">{r.count}</td>}
                <td className="px-4 py-3 font-semibold">₹{r.value.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StockReport;
